import type { BenefitCategory, BenefitTag } from "@yutai/shared"
import type { CSSProperties } from "react"
import { Link } from "react-router-dom"
import type { AllocationFrequency } from "../../lib/allocationFrequency.js"
import type { MarketCapTier } from "../../lib/marketCapTier.js"
import type { RelevanceLevel } from "../../lib/relevance.js"
import styles from "./FilterPanel.module.css"
import { SearchBar } from "./SearchBar.js"
import type { FilterCounts } from "./filterBenefits.js"
import {
  ALLOCATION_FREQUENCY_OPTIONS,
  CAP_TIER_OPTIONS,
  FILTER_CATEGORIES,
  FILTER_MONTHS,
  FILTER_TAGS,
  HOLDING_YEAR_OPTIONS,
  MAX_ACQUISITION_AMOUNT_OPTIONS,
  MIN_BENEFIT_VALUE_OPTIONS,
  MIN_YIELD_OPTIONS,
  RELEVANCE_LEVEL_OPTIONS,
} from "./filterConstants.js"
import type { Filters } from "./useBenefits.js"

type FilterVariant = "full" | "categories" | "details"
type ResetScope = "all" | "details" | "exceptQuery"

interface Props {
  filters: Filters
  counts: FilterCounts | null
  onChange: (partial: Partial<Filters>) => void
  variant?: FilterVariant
  resetScope?: ResetScope
  showSearch?: boolean
  // Distinguishes this instance's radio `name` attributes from any other
  // FilterPanel mounted at the same time (e.g. the sidebar and the mobile
  // drawer are both in the DOM simultaneously) — same-named radios across
  // separate React-controlled groups fight the browser's native grouping
  // and need two clicks to register.
  namePrefix?: string
}

function Count({ value }: { value: number | undefined }) {
  if (value === undefined) return null
  return <span className={styles.count}>{value}</span>
}

export function FilterPanel({
  filters,
  counts,
  onChange,
  variant = "full",
  resetScope = "all",
  showSearch = true,
  namePrefix = "",
}: Props) {
  const showCategories = variant === "full" || variant === "categories"
  const showDetails = variant === "full" || variant === "details"

  function toggleCategory(cat: BenefitCategory) {
    const next = filters.categories.includes(cat)
      ? filters.categories.filter((c) => c !== cat)
      : [...filters.categories, cat]
    onChange({ categories: next })
  }

  function toggleTag(tag: BenefitTag) {
    const next = filters.tags.includes(tag)
      ? filters.tags.filter((t) => t !== tag)
      : [...filters.tags, tag]
    onChange({ tags: next })
  }

  function toggleMonth(m: number) {
    const next = filters.months.includes(m)
      ? filters.months.filter((x) => x !== m)
      : [...filters.months, m]
    onChange({ months: next })
  }

  function toggleDeliveryMonth(m: number) {
    const next = filters.deliveryMonths.includes(m)
      ? filters.deliveryMonths.filter((x) => x !== m)
      : [...filters.deliveryMonths, m]
    onChange({ deliveryMonths: next })
  }

  function toggleIndustry(industry: string) {
    const next = filters.industries.includes(industry)
      ? filters.industries.filter((i) => i !== industry)
      : [...filters.industries, industry]
    onChange({ industries: next })
  }

  function toggleRelevance(level: RelevanceLevel) {
    const next = filters.relevance.includes(level)
      ? filters.relevance.filter((r) => r !== level)
      : [...filters.relevance, level]
    onChange({ relevance: next })
  }

  function toggleCapTier(tier: MarketCapTier) {
    const next = filters.capTiers.includes(tier)
      ? filters.capTiers.filter((t) => t !== tier)
      : [...filters.capTiers, tier]
    onChange({ capTiers: next })
  }

  function toggleAllocationFrequency(freq: AllocationFrequency) {
    const next = filters.allocationFrequency.includes(freq)
      ? filters.allocationFrequency.filter((f) => f !== freq)
      : [...filters.allocationFrequency, freq]
    onChange({ allocationFrequency: next })
  }

  function resetFilters() {
    if (resetScope === "details") {
      onChange({
        tags: [],
        months: [],
        deliveryMonths: [],
        industries: [],
        minBenefitValue: null,
        longTermBonus: null,
        relevance: [],
        minDividendYield: null,
        minBenefitYield: null,
        minCombinedYield: null,
        capTiers: [],
        allocationFrequency: [],
        maxAcquisitionAmount: null,
      })
      return
    }
    onChange({
      categories: [],
      tags: [],
      months: [],
      deliveryMonths: [],
      industries: [],
      minBenefitValue: null,
      longTermBonus: null,
      relevance: [],
      minDividendYield: null,
      minBenefitYield: null,
      minCombinedYield: null,
      capTiers: [],
      allocationFrequency: [],
      maxAcquisitionAmount: null,
      ...(resetScope === "all" ? { query: "" } : {}),
    })
  }

  return (
    <div className={styles.panel}>
      {showSearch && (
        <section className={styles.searchSection}>
          <SearchBar value={filters.query} onChange={(query) => onChange({ query })} />
        </section>
      )}

      {showCategories && (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>カテゴリ</h3>
          {FILTER_CATEGORIES.map(({ value, label }) => (
            <label key={value} className={styles.checkLabel}>
              <span className={styles.checkMain}>
                <input
                  type="checkbox"
                  className={styles.categoryCheck}
                  style={
                    {
                      "--cat-bg": `var(--color-badge-${value})`,
                      "--cat-text": `var(--color-badge-${value}-text)`,
                    } as CSSProperties
                  }
                  checked={filters.categories.includes(value)}
                  onChange={() => toggleCategory(value)}
                />
                {label}
              </span>
              <Count value={counts?.categories[value]} />
            </label>
          ))}
        </section>
      )}

      {showDetails && (
        <>
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>タグ</h3>
            <div className={styles.tagGrid}>
              {FILTER_TAGS.map(({ value, label }) => {
                const active = filters.tags.includes(value)
                return (
                  <button
                    key={value}
                    type="button"
                    className={styles.tagChip}
                    aria-pressed={active}
                    onClick={() => toggleTag(value)}
                  >
                    {label}
                    <span className={styles.tagChipCount}>{counts?.tags[value] ?? 0}</span>
                  </button>
                )
              })}
            </div>
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              会社規模
              <Link
                to="/disclaimer"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.helpLink}
                title="会社規模の区分の定義について"
              >
                ?
              </Link>
            </h3>
            {CAP_TIER_OPTIONS.map(({ value, label }) => (
              <label key={value} className={styles.checkLabel}>
                <span className={styles.checkMain}>
                  <input
                    type="checkbox"
                    checked={filters.capTiers.includes(value)}
                    onChange={() => toggleCapTier(value)}
                  />
                  {label}
                </span>
                <Count value={counts?.capTier[value]} />
              </label>
            ))}
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>権利確定月</h3>
            <div className={styles.monthGrid}>
              {FILTER_MONTHS.map((m) => (
                <label key={m} className={styles.checkLabel}>
                  <span className={styles.checkMain}>
                    <input
                      type="checkbox"
                      checked={filters.months.includes(m)}
                      onChange={() => toggleMonth(m)}
                    />
                    {m}月
                  </span>
                  <Count value={counts?.recordMonths[m]} />
                </label>
              ))}
            </div>
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>優待送付月</h3>
            <div className={styles.monthGrid}>
              {FILTER_MONTHS.map((m) => (
                <label key={m} className={styles.checkLabel}>
                  <span className={styles.checkMain}>
                    <input
                      type="checkbox"
                      checked={filters.deliveryMonths.includes(m)}
                      onChange={() => toggleDeliveryMonth(m)}
                    />
                    {m}月
                  </span>
                  <Count value={counts?.deliveryMonths[m]} />
                </label>
              ))}
            </div>
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>割当実施回数</h3>
            {ALLOCATION_FREQUENCY_OPTIONS.map(({ value, label }) => (
              <label key={value} className={styles.checkLabel}>
                <span className={styles.checkMain}>
                  <input
                    type="checkbox"
                    checked={filters.allocationFrequency.includes(value)}
                    onChange={() => toggleAllocationFrequency(value)}
                  />
                  {label}
                </span>
                <Count value={counts?.allocationFrequency[value]} />
              </label>
            ))}
          </section>

          {counts && Object.keys(counts.industries).length > 0 && (
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>業種</h3>
              {Object.keys(counts.industries)
                .sort((a, b) => a.localeCompare(b, "ja"))
                .map((industry) => (
                  <label key={industry} className={styles.checkLabel}>
                    <span className={styles.checkMain}>
                      <input
                        type="checkbox"
                        checked={filters.industries.includes(industry)}
                        onChange={() => toggleIndustry(industry)}
                      />
                      {industry}
                    </span>
                    <Count value={counts.industries[industry]} />
                  </label>
                ))}
            </section>
          )}

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>優待金額</h3>
            {MIN_BENEFIT_VALUE_OPTIONS.map(({ value, label }) => (
              <label key={String(value)} className={styles.checkLabel}>
                <span className={styles.checkMain}>
                  <input
                    type="radio"
                    name={`${namePrefix}${variant === "details" ? "minValueMobile" : "minBenefitValue"}`}
                    checked={filters.minBenefitValue === value}
                    onChange={() => onChange({ minBenefitValue: value })}
                  />
                  {label}
                </span>
                <Count value={counts?.minBenefitValue[String(value)]} />
              </label>
            ))}
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>最低取得額</h3>
            {MAX_ACQUISITION_AMOUNT_OPTIONS.map(({ value, label }) => (
              <label key={String(value)} className={styles.checkLabel}>
                <span className={styles.checkMain}>
                  <input
                    type="radio"
                    name={`${namePrefix}${variant === "details" ? "maxAcquisitionAmountMobile" : "maxAcquisitionAmount"}`}
                    checked={filters.maxAcquisitionAmount === value}
                    onChange={() => onChange({ maxAcquisitionAmount: value })}
                  />
                  {label}
                </span>
                <Count value={counts?.maxAcquisitionAmount[String(value)]} />
              </label>
            ))}
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>配当利回り</h3>
            {MIN_YIELD_OPTIONS.map(({ value, label }) => (
              <label key={String(value)} className={styles.checkLabel}>
                <span className={styles.checkMain}>
                  <input
                    type="radio"
                    name={`${namePrefix}${variant === "details" ? "minDividendYieldMobile" : "minDividendYield"}`}
                    checked={filters.minDividendYield === value}
                    onChange={() => onChange({ minDividendYield: value })}
                  />
                  {label}
                </span>
                <Count value={counts?.minDividendYield[String(value)]} />
              </label>
            ))}
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>優待利回り</h3>
            {MIN_YIELD_OPTIONS.map(({ value, label }) => (
              <label key={String(value)} className={styles.checkLabel}>
                <span className={styles.checkMain}>
                  <input
                    type="radio"
                    name={`${namePrefix}${variant === "details" ? "minBenefitYieldMobile" : "minBenefitYield"}`}
                    checked={filters.minBenefitYield === value}
                    onChange={() => onChange({ minBenefitYield: value })}
                  />
                  {label}
                </span>
                <Count value={counts?.minBenefitYield[String(value)]} />
              </label>
            ))}
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>総合利回り</h3>
            {MIN_YIELD_OPTIONS.map(({ value, label }) => (
              <label key={String(value)} className={styles.checkLabel}>
                <span className={styles.checkMain}>
                  <input
                    type="radio"
                    name={`${namePrefix}${variant === "details" ? "minYieldMobile" : "minCombinedYield"}`}
                    checked={filters.minCombinedYield === value}
                    onChange={() => onChange({ minCombinedYield: value })}
                  />
                  {label}
                </span>
                <Count value={counts?.minCombinedYield[String(value)]} />
              </label>
            ))}
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>長期保有特典</h3>
            <label className={styles.checkLabel}>
              <span className={styles.checkMain}>
                <input
                  type="radio"
                  name={`${namePrefix}${variant === "details" ? "longTermBonusMobile" : "longTermBonus"}`}
                  checked={filters.longTermBonus === null}
                  onChange={() => onChange({ longTermBonus: null })}
                />
                指定なし
              </span>
            </label>
            {HOLDING_YEAR_OPTIONS.map(({ value, label }) => (
              <label key={value} className={styles.checkLabel}>
                <span className={styles.checkMain}>
                  <input
                    type="radio"
                    name={`${namePrefix}${variant === "details" ? "longTermBonusMobile" : "longTermBonus"}`}
                    checked={filters.longTermBonus === value}
                    onChange={() => onChange({ longTermBonus: value })}
                  />
                  {label}
                </span>
                <Count value={counts?.longTermBonus[String(value)]} />
              </label>
            ))}
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>事業と優待の関連</h3>
            {RELEVANCE_LEVEL_OPTIONS.map(({ value, label }) => (
              <label key={value} className={styles.checkLabel}>
                <span className={styles.checkMain}>
                  <input
                    type="checkbox"
                    checked={filters.relevance.includes(value)}
                    onChange={() => toggleRelevance(value)}
                  />
                  {label}
                </span>
                <Count value={counts?.relevance[value]} />
              </label>
            ))}
          </section>

          <button type="button" className={styles.resetButton} onClick={resetFilters}>
            {resetScope === "details" ? "詳細フィルタをリセット" : "フィルタをリセット"}
          </button>
        </>
      )}
    </div>
  )
}
